import { MuiTooltip } from "@/components/ui/mui-tooltip";
import { MuiTypography } from "@/components/ui/mui-typography";
import { UNSPECIFIED_MANUFACTURER_ID, type CatalogDetailItem } from "@/lib/catalog-item-detail";
import { previewVariantOrder } from "@/lib/catalog-item-variants";
import { truncateText } from "@/lib/truncate-text";
import { ItemEiLogo, ItemEiTierChips, ItemStatusOverlay } from "./item-badges";
import { ItemShortFactLine, VariantThumbStrip } from "./item-compact-card-parts";
import { ItemImage } from "./item-image";

// The Add Variant dialog's left column: a card preview of the item the dialog
// was opened from, following the variant strip inside it.
//
// Source: `AddVariantIsland/components/item-details.tsx`. It looks like the
// catalog card and is not one — four things differ, and each is the donor's:
//
//   1. **The badge chain is the details page's**, not the list's. This card asks
//      `isCore && isApproved` and `!isApproved` and has no Not Recommended arm,
//      so an item flagged against the business shows Core Item here while its
//      card in the list shows Not Recommended (`item-details.tsx:88-92,
//      119-131`). Hence `rule="detail"` on the overlay.
//   2. **The detail lines use the abbreviated labels** — `Model #`, `Item #` —
//      which `ItemFields` explicitly does not: those are the *table's* headings
//      on the list surface. Two surfaces, two label sets, both the donor's. It
//      is also why this card composes its own lines rather than `ItemFields`,
//      which would bring the spelled-out labels and the `N/A` placeholder with
//      it.
//   3. **The names are cut by character count, not by CSS** — 25 for the
//      manufacturer, 80 for the item — with a tooltip carrying the whole thing.
//      See `@/lib/truncate-text` for why the two are not interchangeable.
//   4. **The tile strip is the third in this codebase**: 28px, an eight-column
//      grid, current-item-first, and *not buttons* in the donor. See
//      `VariantThumbStrip`, which the Item Search island's card shares (#798).
//
// The hero is a real picture here, at the donor's 250px, and that is #254's own
// reading rather than an extension of the images slice: the ticket asks this
// column for "hero image (height 250, `?w=256`, empty-image fallback)" and
// excludes exactly one image surface from the slice by name — the dialog's
// *uploader* section. The details page's own gallery slot stays a placeholder
// until #303, as it is.
//
// `ItemMaxPrice` sits between the name and the four lines in the donor and is
// **not ported**, because it draws nothing: it returns `null` unless
// `showTargetCost` is passed, and this card does not pass it
// (`item-max-price.tsx:15-17`, `item-details.tsx:214`). Dead in the source, so
// there is nothing to carry.

/**
 * How wide this card's hero is actually drawn, as a `sizes` list — derived from
 * the layout above it, which is the only place the number exists.
 *
 * The hero is a stated-height box (`heightPx={250}`), so it fixes no width of
 * its own and cannot state its own `sizes`; see `ItemImageBox`'s `slotSizes`.
 * The chain, all of it CSS in this file and `add-variant-dialog.tsx`:
 *
 * - the dialog is `w-[calc(100%-14rem)]` capped at `max-w-[1200px]`, so its
 *   paper is `min(1200, 100vw - 224)`;
 * - its body has `px-10`, leaving a grid `80px` narrower;
 * - the grid is `grid-cols-12 gap-10`, and this card is `md:col-span-4` — four
 *   columns plus the three gaps between them, `(grid - 440) / 3 + 120`;
 * - the card's own `border-4` takes `8px` off the picture inside it, and its
 *   `min-w-[260px]` floors the whole thing at `252px` of picture.
 *
 * **Measured against the rendered dialog rather than trusted from that
 * arithmetic**, because two of the five stops are floors rather than formulas.
 * Picture width by viewport: 252px from `md` (900px) up to 1164px, where the
 * `min-w` floor is what wins; `calc(33.333vw - 136px)` from there to 1424px,
 * where the paper stops growing; 339px above that. Below `md` the card is
 * `col-span-12` and takes the body's full width, `100vw - 312px` — until the
 * form column's own min-content stops the grid shrinking at 432px of picture
 * (~744px of viewport), which is the last stop.
 *
 * That final 432px is the one number here that is a measurement of *content*
 * rather than of a rule, so it is the one that can drift: a narrower form would
 * let the grid shrink further and this would overstate the slot. Overstating is
 * the safe direction — one rung wider, never a rung too small — and the stop is
 * kept rather than dropped because dropping it understates by nearly a rung at
 * phone widths.
 *
 * Stated to the rung rather than rounded up to one number, because the ladder's
 * steps are 2x: overstating a 252px slot as 339px is the difference between
 * `w256` and `w512` at 1x, and between `w512` and `w1024` at 2x.
 */
const PREVIEW_HERO_SIZES = [
  "(min-width: 1424px) 339px",
  "(min-width: 1164px) calc(33.333vw - 136px)",
  "(min-width: 900px) 252px",
  "(min-width: 744px) calc(100vw - 312px)",
  "432px",
].join(", ");

export interface AddVariantPreviewCardProps {
  /**
   * The item the dialog was opened from — the strip's ordering key.
   *
   * Deliberately not `item.id`: the donor orders on the item the dialog *opened*
   * from (`itemIds.indexOf(item.id)` against its unchanging `itemId` prop), so
   * clicking through the strip moves the highlight and never the tiles.
   */
  baseItemId: number;
  /** The variant currently on show — every part of this card follows it. */
  item: CatalogDetailItem;
  /** The whole group, as the details read carries it. */
  variants: readonly CatalogDetailItem[];
  onSelectVariant: (itemId: number) => void;
}

export function AddVariantPreviewCard({
  baseItemId,
  item,
  variants,
  onSelectVariant,
}: AddVariantPreviewCardProps) {
  const manufacturerName = truncateText(item.manufacturer.name, 25);
  // The full name plus the *truncated* manufacturer — the donor builds it from
  // `name` and `manufacturerName` in exactly that asymmetry
  // (`item-details.tsx:206-209`).
  //
  // The Unspecified manufacturer is left out, which is unreachable from this
  // dialog (the action menu refuses Add Variant for it before the dialog opens)
  // and carried anyway. Matched on the id rather than the donor's name compare:
  // it is the same row after the migration (ADR-0004), and the id is what the
  // rest of this codebase already treats as its identity.
  const nameTooltip =
    item.manufacturer.id === UNSPECIFIED_MANUFACTURER_ID
      ? item.name
      : `${item.name} - ${manufacturerName}`;

  return (
    <div
      data-testid="add-variant-preview"
      // MUI's outlined `Card` with `borderWidth: 4px` in `transparent` — a 4px
      // inset the shadowed box below sits inside, which is what keeps the shadow
      // off the column's edge. `overflow: unset` is the donor's too, and it is
      // what lets the EI mark hang over the picture's lower edge.
      className="relative flex min-w-[260px] flex-col rounded-[4px] border-4 border-transparent"
    >
      <div className="flex h-full flex-col shadow-[1px_3px_8px_0_rgba(0,0,0,0.25)]">
        {/* The donor's `CoreItemBadge` / `ItemStatusPendingChip` pair. Both
            position themselves against the card above, which is the positioned
            ancestor — the donor's `Stack` wrapper only ever held them. */}
        <ItemStatusOverlay item={item} rule="detail" />
        <div data-testid="add-variant-preview-hero" className="border-lennar-divider border-b">
          <ItemImage
            image={item.heroImage}
            itemName={item.name}
            heightPx={250}
            slotSizes={PREVIEW_HERO_SIZES}
            pendingApproval={!item.isApproved}
          />
        </div>
        <ItemEiLogo item={item} placement="preview" />
        {/* `CardContent sx={{ px: 2, py: 1, flex: '1 1 0%' }}` around a
            `justifyContent: flex-start` column. */}
        <div className="flex flex-1 flex-col items-start justify-start px-4 py-2">
          {/* The gate and the ordering are `previewVariantOrder`'s, under a
              unit test. */}
          <VariantThumbStrip
            arrangement="grid"
            thumbs={previewVariantOrder(baseItemId, variants)}
            currentId={item.id}
            onSelect={onSelectVariant}
            testIdPrefix="add-variant-preview"
          />
          <ItemEiTierChips item={item} />
          {/* `subtitle1` at the donor's `fontWeight: 600` override — 16px / 1.5,
              semibold — inside a row with 8px under it. */}
          <div className="flex w-full items-center justify-between pb-2">
            <MuiTypography
              data-testid="add-variant-preview-manufacturer"
              variant="subtitle1"
              component="span"
              color="primary"
              className="pt-2 font-semibold break-words"
            >
              {manufacturerName}
            </MuiTypography>
          </div>
          <div className="w-full">
            {/* `body1` in black, with 4px under it. The tooltip is on a span
                inside the paragraph, which is where the donor puts it. */}
            <MuiTypography variant="body1" color="primary" className="pb-1 break-words">
              {/* No `cursor-*`: the donor wraps a bare `<span>` and names no
                  cursor (`item-details.tsx:226-230`), so the pointer stays the
                  text cursor the surrounding paragraph gives it. */}
              <MuiTooltip
                title={nameTooltip}
                side="top"
                asChild
                data-testid="add-variant-preview-name"
              >
                <span>{truncateText(item.name, 80)}</span>
              </MuiTooltip>
            </MuiTypography>
            <ItemShortFactLine
              testId="add-variant-preview-line-category"
              label="Category"
              value={item.category?.name}
            />
            <ItemShortFactLine
              testId="add-variant-preview-line-model"
              label="Model #"
              value={item.modelNumber}
            />
            <ItemShortFactLine
              testId="add-variant-preview-line-jde"
              label="Item #"
              value={item.jdeItemNumber}
            />
            <ItemShortFactLine
              testId="add-variant-preview-line-finish"
              label="Color"
              value={item.finish?.name}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
